import * as React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  TableChart as TableChartIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import type { ChunkDocuments, ChunkMetadata } from "./types";
import { ChunkContentView } from "./ChunkContentView";
import {
  getChunkDisplayTitle,
  getChunkModalTitle,
  getChunkTypeLabel,
  normalizeChunkType,
} from "./chunkUtils";
import { downloadKbDocument } from "../../services/api";
import toast from "react-hot-toast";

interface DocumentsSidebarProps {
  open: boolean;
  onClose: () => void;
  documents: ChunkDocuments | undefined;
}

/** Formatta la data per visualizzazione (es. 2006-04-03 → 03/04/2006) */
function formatChunkDate(datetime: string | undefined): string {
  if (!datetime) return "—";
  const d = new Date(datetime);
  if (Number.isNaN(d.getTime())) return datetime;
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function ChunkTypeIcon({
  meta,
}: {
  meta: ChunkMetadata;
}) {
  if (meta.golden_standard) {
    return (
      <CollectionsBookmarkIcon
        sx={{ fontSize: "2rem", color: "white", flexShrink: 0 }}
      />
    );
  }

  const iconSx = { fontSize: "2rem", color: "white", flexShrink: 0 };
  switch (normalizeChunkType(meta.type)) {
    case "table":
      return <TableChartIcon sx={iconSx} />;
    case "image":
      return <ImageIcon sx={iconSx} />;
    default:
      return <PdfIcon sx={iconSx} />;
  }
}

const sidebarWidth = 396;

export const DocumentsSidebar: React.FC<DocumentsSidebarProps> = ({
  open,
  onClose,
  documents,
}) => {
  const [chunkModalContent, setChunkModalContent] =
    React.useState<ChunkMetadata | null>(null);
  const [slideIn, setSlideIn] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setSlideIn(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setSlideIn(false);
  }, [open]);

  if (!documents) return null;

  const docEntries = Object.entries(documents);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="permanent"
      color="background.default"
      PaperProps={{
        sx: {
          width: sidebarWidth,
          borderRadius: 0,
          borderLeft: 0,
          transform: slideIn ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.35s ease-out",
          visibility: "visible",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            gap: 1,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                fontWeight: 500,
                fontSize: "1rem",
              }}
            >
              Fonti Citate
            </Typography>
          </Box>
        </Box>

        {/* Documents List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            pt: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {docEntries.map(([id, metadata]) => {
            const title = getChunkDisplayTitle(metadata);
            const date = formatChunkDate(metadata?.datetime);
            const typeLabel = getChunkTypeLabel(metadata.type);
            const showFileSubtitle =
              metadata.file_title?.trim() &&
              metadata.file_title.trim() !== title;

            return (
              <Paper
                key={id}
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: "rgba(255, 255, 255, 0)",
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.04)",
                  },
                }}
              >
                {/* Icona per tipo chunk / golden standard */}
                <ChunkTypeIcon meta={metadata} />

                {/* Document Info */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    minWidth: 0,
                  }}
                >
                  <Tooltip
                    title={
                      showFileSubtitle
                        ? `${title}\n${metadata.file_title}`
                        : title
                    }
                    enterDelay={300}
                    placement="top-start"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </Typography>
                  </Tooltip>
                  {showFileSubtitle && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.55)",
                        fontSize: "0.7rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {metadata.file_title}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        fontSize: "0.75rem",
                      }}
                    >
                      {typeLabel} · {date}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.6)",
                        fontSize: "0.7rem",
                        flexShrink: 0,
                      }}
                    >
                      {metadata?.page_idx
                        ? "Pagina: " + metadata?.page_idx
                        : ""}
                    </Typography>
                  </Box>
                </Box>

                {/* Download: solo per documenti non golden standard; abilitato se id_file/file_id presente */}
                {!metadata.golden_standard &&
                  (() => {
                    const fileId = metadata.file_id;
                    return (
                      <Tooltip
                        title={
                          fileId
                            ? "Scarica file"
                            : "Download non disponibile per questo documento"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!fileId}
                            onClick={() => {
                              if (fileId) {
                                downloadKbDocument(
                                  fileId,
                                  metadata.file_title
                                ).catch(() =>
                                  toast.error("Errore durante il download")
                                );
                              }
                            }}
                            sx={{
                              color: "#FFFFFF",
                              flexShrink: 0,
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255, 255, 255, 0.4)",
                              },
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })()}
                {/* Visibility Icon - apre modale con testo citato del chunk */}
                <IconButton
                  size="small"
                  onClick={() => setChunkModalContent(metadata)}
                  sx={{
                    color: "#FFFFFF",
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Modale: testo citato del chunk */}
      <Dialog
        open={chunkModalContent !== null}
        onClose={() => setChunkModalContent(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            pr: 1,
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" component="span" sx={{ fontSize: "1.1rem" }}>
              {chunkModalContent
                ? getChunkModalTitle(chunkModalContent.type)
                : "Testo citato"}
            </Typography>
            {chunkModalContent && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getChunkDisplayTitle(chunkModalContent)}
                {chunkModalContent.file_title &&
                  chunkModalContent.file_title !==
                    getChunkDisplayTitle(chunkModalContent) &&
                  ` · ${chunkModalContent.file_title}`}
                {chunkModalContent.page_idx != null &&
                  ` · Pag. ${chunkModalContent.page_idx}`}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setChunkModalContent(null)}
            aria-label="Chiudi"
            sx={{ mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {chunkModalContent && (
            <ChunkContentView meta={chunkModalContent} showMeta />
          )}
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};
